import { Badge, HStack, Tab } from "@chakra-ui/react";
import { useExternalIDsRetrieveSuspense } from "gen/endpoints/external-id/external-id";
import { useHypotheticalWaterfallIRRBreakdownRetrieveSuspense } from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useInvestorClassListSuspense } from "gen/endpoints/investor-class/investor-class";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FundDropdown } from "shared/components/input/dropdowns/FundDropdown";
import { InvestorClassDropdown } from "shared/components/input/dropdowns/InvestorClassDropdown";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { isAutoGroupColumn } from "shared/components/table/data/DataTableUtils";
import { useQueryParams } from "shared/hooks/useQueryParams";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { NonNullStringParam } from "shared/utils/QueryParamUtils";
import { useQueryParam } from "use-query-params";

export const HypotheticalWaterfallIRRBreakdown: React.FC = () => {
  const { t } = useTranslation(["financialReporting", "common"]);
  const { fundFamilyId, hypotheticalWaterfallId, endOfPeriodReportId } =
    useRequiredStringParams();

  const {
    security_ownership: securityOwnershipId,
    investor_entity: investorEntityId,
  } = useQueryParams();

  const [fundId, setFundId] = useQueryParam("fund", NonNullStringParam);
  const [investorClassId, setInvestorClassId] = useQueryParam(
    "investor_class",
    NonNullStringParam,
  );

  const { data: irrData } =
    useHypotheticalWaterfallIRRBreakdownRetrieveSuspense(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        fund_id: fundId,
        investor_class_id: investorClassId,
        security_ownership_id: securityOwnershipId,
        investor_entity_id: investorEntityId,
      },
    );
  const [selectedTab, setSelectedTab] = useState(0);

  const { data: funds } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    investor_entity_id: investorEntityId,
    investor_class_id: investorClassId,
    commitment_id: securityOwnershipId,
  });

  const { data: investorClasses } = useInvestorClassListSuspense(fundFamilyId, {
    investor_entity_id: investorEntityId,
  });

  const tableData =
    selectedTab === 0
      ? irrData.gross_irr_breakdown_table_data
      : irrData.net_irr_breakdown_table_data;

  const { data: externalIdList } =
    useExternalIDsRetrieveSuspense(investorEntityId);
  return (
    <PageContent>
      <PageHeader
        title={irrData.name}
        badge={
          <HStack spacing="8px">
            {externalIdList.map((externalID) => (
              <Badge key={externalID.id}>{externalID.name}</Badge>
            ))}
          </HStack>
        }
        backButton={{
          title: t("financialReporting:hypotheticalWaterfall"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .financialReporting({})
            .hypotheticalWaterfall({
              hypotheticalWaterfallId,
              endOfPeriodReportId,
            })
            .review({})
            .performance({}).$,
        }}
      />
      <PageBody>
        <TableBox isFullWidth={true} key={selectedTab}>
          <TableBoxHeader>
            <TableBoxTabs index={selectedTab} onChange={setSelectedTab}>
              <Tab>{t("financialReporting:irrBreakdown.gross")}</Tab>
              <Tab>{t("financialReporting:irrBreakdown.net")}</Tab>
            </TableBoxTabs>
            <DataTableBoxActions>
              <FundDropdown
                fundId={fundId}
                onChangeFundId={setFundId}
                funds={funds}
              />
              {investorEntityId && (
                <InvestorClassDropdown
                  investorClassId={investorClassId}
                  onChangeInvestorClassId={setInvestorClassId}
                  investorClasses={investorClasses}
                />
              )}
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={tableData}
              showTotalRow={false}
              valueGetter={(params) => {
                if (
                  isAutoGroupColumn(params.column) &&
                  params.node?.isRowPinned()
                ) {
                  return params.node.data?.cells["total_row_name"];
                }
              }}
            />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
